import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Eye, EyeOff, FlaskConical } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import apiService from '../services/api.service';
import { toast } from 'sonner';

type FlagStatus = 'hidden' | 'beta' | 'released';

interface FeatureFlag {
  flag_key: string;
  status: FlagStatus;
  updated_at: string | null;
}

// Sections pilotables de l'app Athlète (les autres — Comp, Profil — sont toujours visibles).
const ATHLETE_SECTIONS = ['athlete_feed', 'athlete_stats', 'athlete_log'];

const AdminAppFeatureFlags: React.FC = () => {
  const { t } = useTranslation();
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/app-config/flags');
      setFlags(res.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('featureFlags.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const statusOf = (key: string): FlagStatus => {
    const f = flags.find((x) => x.flag_key === key);
    return f ? f.status : 'released';
  };

  const setStatus = async (key: string, status: FlagStatus) => {
    if (statusOf(key) === status) return;
    setSavingKey(key);
    try {
      await apiService.put(`/app-config/flags/${key}`, { status });
      setFlags((prev) => {
        const existing = prev.find((x) => x.flag_key === key);
        if (existing) {
          return prev.map((x) => (x.flag_key === key ? { ...x, status } : x));
        }
        return [...prev, { flag_key: key, status, updated_at: null }];
      });
      toast.success(t('featureFlags.saved'));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('featureFlags.saveError'));
    } finally {
      setSavingKey(null);
    }
  };

  const options: { value: FlagStatus; label: string; icon: React.ReactNode; active: string }[] = [
    { value: 'released', label: t('featureFlags.released'), icon: <Eye size={15} />, active: 'bg-success/20 text-success border-success/40' },
    { value: 'beta', label: t('featureFlags.beta'), icon: <FlaskConical size={15} />, active: 'bg-warning/20 text-warning border-warning/40' },
    { value: 'hidden', label: t('featureFlags.hidden'), icon: <EyeOff size={15} />, active: 'bg-white/[0.10] text-foreground border-white/30' },
  ];

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div>
        <h1 className="text-xl md:text-3xl font-bold">{t('featureFlags.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('featureFlags.subtitle')}</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 size={32} className="animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 divide-y divide-border">
            {ATHLETE_SECTIONS.map((key) => {
              const current = statusOf(key);
              return (
                <div
                  key={key}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4"
                >
                  <div>
                    <div className="font-semibold">{t(`featureFlags.sections.${key}`)}</div>
                    <div className="text-sm text-muted-foreground">
                      {t(`featureFlags.sectionsDesc.${key}`)}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {options.map((opt) => {
                      const isActive = current === opt.value;
                      return (
                        <button
                          key={opt.value}
                          disabled={savingKey === key}
                          onClick={() => setStatus(key, opt.value)}
                          className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                            isActive
                              ? opt.active
                              : 'border-border text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {savingKey === key && isActive ? (
                            <Loader2 size={15} className="animate-spin" />
                          ) : (
                            opt.icon
                          )}
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminAppFeatureFlags;
